/** Format an ISO date as a short relative string ("2m ago", "yesterday", "2026-04-20"). */
export function relativeDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

/** Initials for an entity name, e.g. "Battery Ventures" -> "Ba". */
export function initials(name?: string, n = 2): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, n);
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Escape a value for CSV output. RFC 4180 compatible. */
export function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = Array.isArray(v) ? v.join(', ') : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Build a CSV string from an array of records and a column spec. */
export function toCsv<T>(rows: T[], columns: { key: keyof T | string; label: string; get?: (row: T) => unknown }[]): string {
  const header = columns.map(c => csvEscape(c.label)).join(',');
  const body = rows.map(r =>
    columns.map(c => {
      const val = c.get ? c.get(r) : (r as Record<string, unknown>)[c.key as string];
      return csvEscape(val);
    }).join(','),
  );
  return [header, ...body].join('\n');
}

/** Trigger a CSV download in the browser. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Hash a string into one of a few muted background colors for a logo placeholder. */
export function logoColor(seed?: string): { bg: string; fg: string } {
  const palette = [
    { bg: '#FEE2E2', fg: '#991B1B' },
    { bg: '#DBEAFE', fg: '#1E40AF' },
    { bg: '#E0F2FE', fg: '#075985' },
    { bg: '#FEF3C7', fg: '#92400E' },
    { bg: '#E0E7FF', fg: '#3730A3' },
    { bg: '#DCFCE7', fg: '#166534' },
    { bg: '#F3E8FF', fg: '#6B21A8' },
    { bg: '#FFE4E6', fg: '#9F1239' },
  ];
  let h = 0;
  for (const c of (seed ?? '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}
